/**
 * File Quality Scorecard — counsel-ruled copy + metric config (DEV-3777).
 *
 * Every string a broker reads in this zone lives here. The disclaimer is
 * rendered VERBATIM and non-collapsible per the compliance ruling — do not edit
 * its wording. No badge/tier/recognition/volume copy is permitted anywhere in
 * this zone (counsel bright line).
 */

export const FILE_QUALITY_TITLE = 'File Quality';

export const FILE_QUALITY_SUBTITLE = "Your firm's own file-handling performance";

// Rescope (DEV-4041): a company-level (non-broker) user sees whole-company data,
// so the broker "your firm" framing of the header is reworded to company-level
// wording. The counsel-verbatim disclaimer body below is NOT reworded.
export const FILE_QUALITY_COMPANY_SUBTITLE = "The company's own file-handling performance";

// Stable metric keys — match the backend `metrics` / `sampleSizes` map keys
// exactly. Both halves return all five; each service nulls the ones it does not
// own and the slice merges them.
export const QualityMetricKey = {
  CONDITIONS_CLEARED_WITHIN_SLA_PCT: 'conditionsClearedWithinSlaPct',
  SUBMISSION_COMPLETENESS_PCT: 'submissionCompletenessPct',
  CONDITION_CLEAR_TURN_TIME_MEDIAN_HOURS: 'conditionClearTurnTimeMedianHours',
  FIRST_TIME_RIGHT_PCT: 'firstTimeRightPct',
  BROKER_OWNED_TIME_IN_STATUS_MEDIAN_HOURS: 'brokerOwnedTimeInStatusMedianHours',
};

// How a metric's value is shaped: a percentage, or a duration shown as a
// human-formatted median.
export const QualityMetricFormat = {
  PERCENT: 'PERCENT',
  DURATION: 'DURATION',
};

// The two duration metrics carry the program-mix note when the backend flags it.
export const DURATION_METRIC_KEYS = [
  QualityMetricKey.CONDITION_CLEAR_TURN_TIME_MEDIAN_HOURS,
  QualityMetricKey.BROKER_OWNED_TIME_IN_STATUS_MEDIAN_HOURS,
];

// Display order + per-metric copy. `isNotTracked` flags a metric neither service
// populates yet — it renders "Not yet available", visually distinct from N=10
// insufficient-data. DEV-4005 wires net-new capture for submission-completeness +
// first-time-right, so both are now tracked: a real value renders with its sample
// N, and a still-null value (broker below the N>=10 floor, or capture not yet
// deployed) degrades to the honest insufficient-data state rather than a 0.
// NOTE: no metric sets `isNotTracked: true` today, so the NOT_TRACKED display path
// (and QUALITY_NOT_TRACKED_TEXT) is intentionally retained scaffolding for the next
// not-yet-captured metric to opt into — do not prune it as dead.
export const QUALITY_METRICS = [
  {
    key: QualityMetricKey.CONDITIONS_CLEARED_WITHIN_SLA_PCT,
    label: 'Conditions cleared within SLA',
    format: QualityMetricFormat.PERCENT,
    isNotTracked: false,
  },
  {
    key: QualityMetricKey.CONDITION_CLEAR_TURN_TIME_MEDIAN_HOURS,
    label: 'Condition-clearing turn time',
    format: QualityMetricFormat.DURATION,
    isNotTracked: false,
  },
  {
    key: QualityMetricKey.BROKER_OWNED_TIME_IN_STATUS_MEDIAN_HOURS,
    label: 'Time in status (your shop)',
    format: QualityMetricFormat.DURATION,
    isNotTracked: false,
  },
  {
    key: QualityMetricKey.FIRST_TIME_RIGHT_PCT,
    label: 'Accepted on first submission',
    format: QualityMetricFormat.PERCENT,
    isNotTracked: false,
  },
  {
    key: QualityMetricKey.SUBMISSION_COMPLETENESS_PCT,
    label: 'Submission completeness',
    format: QualityMetricFormat.PERCENT,
    isNotTracked: false,
  },
];

export const QUALITY_SAMPLE_PREFIX = 'n =';

// Shown when a metric is null because the broker is below the backend-enforced
// minimum-sample floor. The absence is rendered honestly — never a fabricated 0.
export const QUALITY_INSUFFICIENT_DATA_TEXT = 'Insufficient data — requires 10 files in the period';

// Shown for metrics that are not tracked by either service yet
// (submission-completeness, first-time-right). Visually distinct from the
// insufficient-data (below-floor) state.
export const QUALITY_NOT_TRACKED_TEXT = 'Not yet available';

// The "what's included" definition for time-in-status (counsel condition): an
// accessible tooltip stating only broker-owned states are counted.
export const TIME_IN_STATUS_DEFINITION =
  'Counts only time the file was in states your shop was responsible for advancing. Time spent in lender or underwriting queues is excluded.';

export const TIME_IN_STATUS_INFO_LABEL = "What's included";

// Rendered on the two DURATION metrics when the backend sets programMixNote.
export const PROGRAM_MIX_NOTE_TEXT =
  'Includes programs that require additional documentation; compare across periods accordingly.';

export const FILE_QUALITY_AS_OF_PREFIX = 'as of';

export const FILE_QUALITY_WINDOW_PREFIX = 'Rolling';

export const FILE_QUALITY_WINDOW_SUFFIX = 'days';

// The approved disclaimer — rendered VERBATIM and non-collapsible at the bottom
// of the card. Do not alter the wording (compliance ruling, DEV-3777).
export const FILE_QUALITY_DISCLAIMER =
  "These figures reflect your firm's own file-handling performance over the rolling period shown. All time-based metrics measure only time the file was actionable by your shop; time spent in lender underwriting queues is excluded. These metrics are provided for your operational reference only. They are not a rating, are not tied to your compensation or loan pricing, are not affected by the volume of business you send us, and do not entitle you to or withhold any benefit. Figures are as of the date shown and may lag recent activity.";

export const FILE_QUALITY_UNAVAILABLE_TEXT = 'Scorecard temporarily unavailable.';
